import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiCheckbox,
  MiControlledTextField,
  MiMenu,
  MiReorderButton,
} from "@miplatform/components";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import {
  IAreaTypeCategoryResponse,
  IAreaTypeResponse,
  IBidArea,
  IBidAreaMaterial,
  ICategoryFilterResponse,
  IMaterialsResponse,
} from "@miplatform/types";
import {
  mdiAccountOff,
  mdiCheck,
  mdiClose,
  mdiPencilOutline,
  mdiPlus,
  mdiSync,
  mdiTrashCanOutline,
} from "@mdi/js";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from "@mui/material";
import { CollapsibleIcon } from "../../../../../components/CollapsibleIcon/CollapsibleIcon";
import { PlaceholderMaterial } from "../../../../../components/PlaceholderMaterial/PlaceholderMaterial";
import { BidMaterial } from "../BidMaterial/BidMaterial";
import { useBidAreaViewModel } from "./useBidAreaViewModel";

export function BidArea({
  area,
  areaType,
  planAreaMaterials,
  isPreview,
  isLoading = false,
  checkedAreas,
  selectedCategory,
  openMaterialResults,
  disableUp,
  disableDown,
  showCategoryFilter,
  openAreaDrawer,
  showLinkedItems,
  linkedMaterialDetails,
  isBidApproved = false,
  handleOpenItemDrawer,
  handleOpenReplaceItemModal,
  handlePlaceholder,
  handleCheckArea,
}: BidPlanAreaProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  const { classes, theme, cx } = useStyles();
  const { control, watch, reset } = useForm({
    mode: "onChange",
    defaultValues: { label: area.label || "" },
  });
  const { label } = watch();

  const {
    dismissedCategories,
    handleRemoveItemFromArea,
    handleUpdateItemQuantity,
    handleRemoveSuppliedByBuilder,
    handleRemoveArea,
    handleUpdateAreaLabel,
    handleMoveArea,
    handleDismissCategory,
  } = useBidAreaViewModel();

  const categories = areaType?.categories || [];
  const isAreaSelected = checkedAreas.some((c) => c.id === area.id);
  const isPlaceHolderSelected = isAreaSelected
    ? selectedCategory?.categoryId
    : undefined;
  const isDisabled = isBidApproved || showCategoryFilter || openAreaDrawer;

  const areaMaterials = area.materials as IBidAreaMaterial[];

  // Filter out placeholder categories that already have a material assigned
  const filteredCategories = useMemo(() => {
    const sorted = [...categories].sort((a, b) => {
      if (a.isRequired !== b.isRequired)
        return Number(b.isRequired) - Number(a.isRequired);
      return a.displayOrder - b.displayOrder;
    });
    const dismissed = dismissedCategories[area.id] || [];
    return sorted.filter(
      (cat) =>
        !areaMaterials?.some((m) => {
          const item = planAreaMaterials.find((am) => am.id === m.materialId);
          return item?.category?.categoryId === cat.categoryId;
        }) && !dismissed.includes(cat.categoryId)
    );
  }, [
    categories,
    areaMaterials,
    planAreaMaterials,
    dismissedCategories,
    area.id,
  ]);

  const handleSaveLabel = () => {
    handleUpdateAreaLabel(area, label);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    reset({ label: area.label || "" });
    setIsEditing(false);
  };

  const getCardMenu = (
    areaMaterial: IBidAreaMaterial | undefined,
    item: IMaterialsResponse
  ) => {
    const options = [
      {
        label: "Replace Item",
        icon: mdiSync,
        action: () =>
          handleOpenReplaceItemModal({
            item: {
              ...item,
              materialId: areaMaterial?.materialId,
              suppliedByBuilder: areaMaterial?.suppliedByBuilder,
              quantity: areaMaterial?.quantity,
            },
            area,
          }),
      },
      {
        label: "Remove Item",
        icon: mdiTrashCanOutline,
        color: theme.palette.red.main,
        action: () => handleRemoveItemFromArea(area, item.id),
      },
    ];

    if (areaMaterial?.suppliedByBuilder) {
      options.push({
        label: "Remove Supplied By Builder",
        icon: mdiAccountOff,
        color: theme.palette.red.main,
        action: () => handleRemoveSuppliedByBuilder(area, item.id),
      });
    }

    return options;
  };

  return (
    <Accordion
      expanded={isExpanded}
      disableGutters
      className={cx(classes.accordion, isAreaSelected && classes.activeBorder)}
    >
      <AccordionSummary className={classes.summary}>
        <Box className={cx(classes.headerBox, isPreview && classes.previewBox)}>
          <Box className={classes.rightHeaderBox}>
            {openMaterialResults && (
              <MiCheckbox
                value={isAreaSelected}
                handleChange={() => handleCheckArea(area, !isAreaSelected)}
                size="small"
                style={{ marginLeft: theme.spacing(-3) }}
              />
            )}
            {isEditing ? (
              <MiControlledTextField
                name="label"
                label={isEditing ? areaType?.name : ""}
                formControl={control}
                disabled={!isEditing}
                readOnly={!isEditing}
                className={classes.labelInput}
              />
            ) : (
              <Typography className={classes.areaLabel}>{label}</Typography>
            )}
            {isEditing && (
              <Box className={classes.cancelSaveBox}>
                <MiButton
                  startIcon={{
                    path: mdiClose,
                    iconSize: 0.7,
                    iconColor: theme.palette.red.main,
                  }}
                  size="small"
                  onClick={handleCancelEdit}
                />
                <MiButton
                  startIcon={{
                    path: mdiCheck,
                    iconSize: 0.7,
                    iconColor: theme.palette.green.main,
                  }}
                  size="small"
                  onClick={handleSaveLabel}
                />
              </Box>
            )}
          </Box>
          <Box className={classes.leftHeaderBox}>
            <Box className={classes.leftActionBox}>
              {!isPreview && (
                <>
                  <MiButton
                    title="Add Items"
                    variant={BUTTON_VARIANTS.TEXT}
                    startIcon={{ path: mdiPlus, iconSize: 0.8 }}
                    onClick={handleOpenItemDrawer}
                    disabled={isDisabled}
                    size="small"
                  />
                  <MiReorderButton
                    onMoveUp={() => handleMoveArea(area, "up")}
                    onMoveDown={() => handleMoveArea(area, "down")}
                    disableUp={disableUp}
                    disableDown={disableDown}
                    disabled={isDisabled}
                  />
                  <CollapsibleIcon
                    isCollapsed={isExpanded}
                    onCollapse={() => setIsExpanded((prev) => !prev)}
                  />
                </>
              )}
            </Box>
            {!isPreview && (
              <MiMenu
                disabled={isEditing || isDisabled}
                data={[
                  {
                    label: "Edit Area Name",
                    icon: mdiPencilOutline,
                    action: () => setIsEditing(true),
                  },
                  {
                    label: "Remove Area",
                    icon: mdiTrashCanOutline,
                    color: theme.palette.red.main,
                    action: () => handleRemoveArea(area),
                  },
                ]}
              />
            )}
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        {areaMaterials?.map((am, index) => {
          const item = planAreaMaterials.find((m) => m.id === am.materialId);
          if (!item) return null;
          const menu = getCardMenu(am, item);

          return (
            <BidMaterial
              key={am.materialId}
              item={item}
              areaMaterial={am}
              area={area}
              isLast={index === areaMaterials.length - 1}
              isDisabled={isDisabled}
              showLinkedItems={showLinkedItems}
              linkedMaterialDetails={linkedMaterialDetails}
              menuData={isBidApproved ? [] : menu}
              handleUpdateItemQuantity={handleUpdateItemQuantity}
            />
          );
        })}
        {filteredCategories.map((cat) => (
          <PlaceholderMaterial
            key={cat.categoryId}
            category={cat}
            isSelected={isPlaceHolderSelected === cat.categoryId}
            disabled={isBidApproved || isPreview || openAreaDrawer || isLoading}
            preview={isPreview}
            status={undefined}
            onClick={() => handlePlaceholder(cat)}
            onDismiss={() => handleDismissCategory(cat.categoryId, area.id)}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

const useStyles = makeStyles()((theme) => ({
  accordion: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.grey.accent}`,
    borderRadius: 4,
    boxShadow: "none",
    "&::before": { content: "none" },
  },
  activeBorder: {
    border: `2px solid ${theme.palette.primary.accent}`,
  },
  summary: {
    backgroundColor: theme.palette.grey.mediumLight,
    borderRadius: 4,
    minHeight: 32,
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
    "& .MuiAccordionSummary-content": {
      margin: 0,
      cursor: "default",
    },
  },
  headerBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  previewBox: {
    opacity: 0.5,
  },
  rightHeaderBox: {
    display: "flex",
    alignItems: "center",
  },
  labelInput: {
    backgroundColor: theme.palette.background.paper,
    "& .MuiOutlinedInput-input": {
      padding: theme.spacing(1),
      fontSize: theme.fontSize.medium,
    },
  },
  areaLabel: {
    padding: theme.spacing(1),
    fontSize: theme.fontSize.medium,
  },
  leftHeaderBox: {
    display: "flex",
    alignItems: "center",
  },
  cancelSaveBox: {
    display: "flex",
    gap: 1,
  },
  leftActionBox: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  accordionDetails: {
    padding: theme.spacing(0.5, 1),
  },
}));

interface BidPlanAreaProps {
  area: IBidArea;
  areaType: IAreaTypeResponse | undefined;
  planAreaMaterials: IMaterialsResponse[];
  isPreview: boolean;
  isLoading: boolean;
  checkedAreas: IBidArea[];
  selectedCategory: Partial<ICategoryFilterResponse>;
  openMaterialResults: boolean;
  disableUp: boolean;
  disableDown: boolean;
  showCategoryFilter: boolean;
  openAreaDrawer: boolean;
  showLinkedItems: boolean;
  linkedMaterialDetails: IMaterialsResponse[];
  isBidApproved?: boolean;
  handleCheckArea: (area: IBidArea, checked: boolean) => void;
  handleOpenItemDrawer: () => void;
  handleOpenReplaceItemModal: (params: {
    item: IMaterialsResponse;
    area: IBidArea;
  }) => void;
  handlePlaceholder: (category: IAreaTypeCategoryResponse) => void;
}
